/**
 * AI Media Command Service
 *
 * Handles browser-side execution of AI media tool commands:
 *  - media_speak         — play TTS audio through speakers (via broadcastCmd)
 *  - camera_capture      — capture a webcam frame (via windowActionService RPA job)
 *  - microphone_record   — record microphone audio (via windowActionService RPA job)
 *
 * media_speak is registered as a command handler (dispatched via broadcastCmd).
 * camera_capture and microphone_record are executed by the windowActionService poll loop.
 *
 * Registration happens once at app startup via registerMediaCommands().
 */

import { API_ORIGIN_URL } from '../api/config';
import { triggerWindowActionPoll } from './windowActionService';

// ---------------------------------------------------------------------------
// Audio playback (speak)
// ---------------------------------------------------------------------------

let _currentAudio: HTMLAudioElement | null = null;

function playSpeechAudio(audioBase64: string): void {
  // Stop any currently playing speech
  if (_currentAudio) {
    _currentAudio.pause();
    _currentAudio.src = '';
    _currentAudio = null;
  }

  const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
  _currentAudio = audio;

  audio.onended = () => {
    if (_currentAudio === audio) _currentAudio = null;
  };

  audio.onerror = (e) => {
    console.error('[MediaCmd] Audio playback error:', e);
    if (_currentAudio === audio) _currentAudio = null;
  };

  audio.play().catch(err => {
    console.error('[MediaCmd] audio.play() failed:', err);
    // Autoplay may be blocked — this is a browser policy limitation
    if (_currentAudio === audio) _currentAudio = null;
  });

  console.log('[MediaCmd] media_speak: playing audio');
}

// ---------------------------------------------------------------------------
// Camera capture (executed in windowActionService poll loop)
// ---------------------------------------------------------------------------

export async function executeCameraCapture(
  _jobId: string,
  _params: { reason?: string },
): Promise<{ success: boolean; result?: any; error?: string }> {
  console.log('[MediaCmd] camera_capture: opening webcam...');

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      audio: false,
    });
  } catch (err: any) {
    const msg = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
      ? 'Camera permission denied. The user must allow camera access when prompted by the browser.'
      : err?.name === 'NotFoundError'
      ? 'No camera device found on this machine.'
      : `Camera access failed: ${err.message}`;
    console.error('[MediaCmd] camera_capture getUserMedia failed:', err);
    return { success: false, error: msg };
  }

  try {
    // Render a single frame to a canvas and export as JPEG
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => video.play().then(resolve).catch(reject);
      video.onerror = () => reject(new Error('Video element error'));
      setTimeout(() => reject(new Error('Camera video timeout')), 8_000);
    });

    // Give the camera a moment to adjust exposure
    await new Promise(resolve => setTimeout(resolve, 300));

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) throw new Error('Canvas 2D context unavailable');

    ctx2d.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const imageBase64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');

    console.log(`[MediaCmd] camera_capture: captured ${canvas.width}×${canvas.height} frame (${imageBase64.length} chars base64)`);

    return {
      success: true,
      result: { imageBase64, width: canvas.width, height: canvas.height },
    };
  } catch (err: any) {
    console.error('[MediaCmd] camera_capture frame capture failed:', err);
    return { success: false, error: `Frame capture failed: ${err.message}` };
  } finally {
    // Always stop the camera stream
    stream.getTracks().forEach(track => track.stop());
  }
}

// ---------------------------------------------------------------------------
// Microphone recording + transcription (executed in windowActionService poll loop)
// ---------------------------------------------------------------------------

export async function executeMicrophoneRecord(
  _jobId: string,
  params: { durationMs: number; language?: string },
): Promise<{ success: boolean; result?: any; error?: string }> {
  const { durationMs = 10_000, language } = params;
  const clampedDuration = Math.max(1_000, Math.min(60_000, durationMs));

  console.log(`[MediaCmd] microphone_record: starting ${clampedDuration}ms recording...`);

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  } catch (err: any) {
    const msg = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
      ? 'Microphone permission denied. The user must allow microphone access when prompted by the browser.'
      : err?.name === 'NotFoundError'
      ? 'No microphone device found on this machine.'
      : `Microphone access failed: ${err.message}`;
    console.error('[MediaCmd] microphone_record getUserMedia failed:', err);
    return { success: false, error: msg };
  }

  try {
    // Pick the best supported format
    const preferredMimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
    ];
    const mimeType = preferredMimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    const chunks: Blob[] = [];

    recorder.ondataavailable = e => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    const stopped = new Promise<void>(resolve => {
      recorder.onstop = () => resolve();
    });

    recorder.start(250); // collect chunks every 250ms
    await new Promise<void>(resolve => setTimeout(resolve, clampedDuration));
    recorder.stop();
    await stopped;

    if (chunks.length === 0) {
      return { success: false, error: 'No audio data was recorded.' };
    }

    const audioBlob = new Blob(chunks, { type: mimeType || 'audio/webm' });
    const audioBuffer = await audioBlob.arrayBuffer();
    const bytes = new Uint8Array(audioBuffer);
    let binary = '';
    const CHUNK = 8192;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    const audioBase64 = btoa(binary);

    // Determine format extension
    const formatMatch = (mimeType || 'audio/webm').match(/audio\/([^;]+)/);
    const audioFormat = formatMatch ? formatMatch[1] : 'webm';

    console.log(`[MediaCmd] microphone_record: recorded ${audioBuffer.byteLength} bytes (${audioFormat}), sending to Whisper...`);

    // Transcribe via backend
    const authToken = localStorage.getItem('authToken');
    const transcribeRes = await fetch(`${API_ORIGIN_URL}/api/media-tools/transcribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ audio_base64: audioBase64, audio_format: audioFormat, language }),
    });

    if (!transcribeRes.ok) {
      const errBody = await transcribeRes.text().catch(() => '');
      throw new Error(`Transcription API error (${transcribeRes.status}): ${errBody.slice(0, 200)}`);
    }

    const transcribeData = await transcribeRes.json() as any;
    const transcript: string = transcribeData.transcript ?? '';
    const detectedLanguage: string | undefined = transcribeData.language;
    const audioDuration: number | undefined = transcribeData.duration_seconds;

    console.log(`[MediaCmd] microphone_record: transcript="${transcript.slice(0, 80)}..." lang=${detectedLanguage}`);

    return {
      success: true,
      result: { transcript, language: detectedLanguage, duration_seconds: audioDuration },
    };
  } catch (err: any) {
    console.error('[MediaCmd] microphone_record failed:', err);
    return { success: false, error: `Recording or transcription failed: ${err.message}` };
  } finally {
    stream.getTracks().forEach(track => track.stop());
  }
}

// ---------------------------------------------------------------------------
// Command registration
// ---------------------------------------------------------------------------

/**
 * Register media commands directly into a registry instance.
 *
 * Called from `initializeAllCommands` in commandRegistryStore so these
 * commands survive re-initialization of the full command map. The previous
 * pattern (mount-time `registerMediaCommands()` with a `_registered` guard)
 * caused `media_speak` to disappear whenever `initializeAllCommands` cleared
 * the command map, resulting in "Command not found: media_speak" errors.
 */
export function registerMediaCommandsInRegistry(registry: {
  registerCommand: (id: any, handler: (...args: any[]) => any) => void;
}): void {
  registry.registerCommand('media_speak', async (params: any) => {
    const { audioBase64 } = params ?? {};
    if (!audioBase64) {
      console.warn('[MediaCmd] media_speak: no audioBase64 in params');
      return;
    }
    playSpeechAudio(audioBase64);
  });

  // window_action_poll: immediately wake up the windowActionService poll loop.
  // Called by listen/camera_capture tools right after creating an RPA job so
  // the frontend picks up the job instantly instead of waiting up to 10s.
  registry.registerCommand('window_action_poll', async () => {
    triggerWindowActionPoll();
  });
}
